import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

import {
  RadioGroupComponent,
  RadioComponent,
  RadioModule,
} from "@vg-constellation/angular-17/radio";
import {
  DropdownModule,
  DropdownMenuData,
  DropdownMenuItem,
} from "@vg-constellation/angular-17/dropdown";
import { InputComponent } from "@vg-constellation/angular-17/input";
import { LabelComponent } from "@vg-constellation/angular-17/label";
import { ButtonComponent } from "@vg-constellation/angular-17/button";
import { LinkComponent, LinkModule } from "@vg-constellation/angular-17/link";
import { HintErrorComponent } from "@vg-constellation/angular-17/hint-error";
import { DatePickerComponent, DatePickerModule } from "@vg-constellation/angular-17/date-picker";
import { IconModule } from "@vg-constellation/angular-17/icon";
import { TooltipComponent, TooltipModule } from "@vg-constellation/angular-17/tooltip";
import { CardComponent } from "@vg-constellation/angular-17/card";

import { BeneficiaryType } from "../../types/BeneficiaryType";
import {
  Beneficiary,
  BeneficiarySectionType,
  DropdownStateChangeEvent,
  getCardDensity,
  EditBeneficiaryPayload,
} from "@models/beneficiary.model";
import { US_STATES_AND_TERRITORIES } from "@weps/shared-constants";
import { Person } from "@services/zok";
import { WebLogger } from "@weps/shared-http";
import { DropdownTypeaheadDirective } from "@weps/shared-ui";
import {
  validateDateOfBirth as validateDateOfBirthUtil,
  DateValidationErrorType,
} from "@weps/shared-validators";
import * as content from "@services/content/guided-flow/account-details-content";

interface RadioStateChangeEvent {
  inputId?: string;
}

@Component({
  selector: "app-input-beneficiary-card",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RadioGroupComponent,
    RadioComponent,
    RadioModule,
    DropdownModule,
    InputComponent,
    LabelComponent,
    ButtonComponent,
    LinkComponent,
    LinkModule,
    HintErrorComponent,
    DatePickerComponent,
    DatePickerModule,
    IconModule,
    TooltipComponent,
    TooltipModule,
    CardComponent,
    DropdownTypeaheadDirective,
  ],
  templateUrl: "./input-beneficiary-card.component.html",
  styleUrls: ["./input-beneficiary-card.component.scss"],
})
export class InputBeneficiaryCardComponent implements OnInit, OnChanges {
  private logEvent(
    eventType: string,
    payload: Record<string, unknown> = {},
    level: "info" | "warn" | "error" = "info",
  ): void {
    WebLogger.log({
      message: `InputBeneficiaryCard: ${eventType}`,
      level,
      payload: { event_type: eventType, ...payload },
    });
  }

  @Input() mode: "add" | "edit" = "add";
  @Input() existingData: Beneficiary | null = null;
  @Input() householdMembers: DropdownMenuItem[] = [];
  @Input() householdPeople: Person[] = [];
  @Input() section: BeneficiarySectionType = "PRIMARY";
  @Input() showCancelOnTypeSelection = false;
  @Input() skipTypeSelection = false;
  @Input() isSaving = false;
  @Input() showSelectError = false;

  @Output() save = new EventEmitter<Beneficiary>();
  @Output() saveEdit = new EventEmitter<EditBeneficiaryPayload>();
  @Output() cancel = new EventEmitter<void>();

  protected readonly content = content;
  protected readonly BeneficiaryType = BeneficiaryType;

  // State tracking
  currentState: "type-selection" | "person-dropdown" | "inline-form" | "trust-placeholder" =
    "type-selection";
  selectedBeneficiaryType: BeneficiaryType | null = null;

  // Form fields
  firstName = "";
  lastName = "";
  dateOfBirth = "";
  stateOfResidence = "";
  selectedPersonId: string | null = null;

  // Validation flags
  validFirstName = true;
  validLastName = true;
  validDateOfBirth = true;
  validStateOfResidence = true;

  // Date constraints
  todayDate: string = new Date().toISOString().split("T")[0];
  minDate: string = "1900-01-01";
  dateOfBirthErrorText = "";

  // Dropdown data
  personDropdownData: DropdownMenuData = { groups: [] };

  // State dropdown options
  stateOptions: DropdownMenuData = {
    groups: [{ items: US_STATES_AND_TERRITORIES.map((s) => ({ labelText: s.name, id: s.code })) }],
  };

  // Card density for responsive sizing
  cardDensity: "small" | "medium" = "medium";

  /** Temporarily hide type change until Trust support is implemented */
  showBeneficiaryTypeChange = false;

  ngOnInit(): void {
    this.buildPersonDropdown();
    this.cardDensity = getCardDensity();

    if (this.mode === "edit" && this.existingData) {
      this.populateFromExistingData();
    } else if (this.skipTypeSelection && this.mode === "add") {
      this.selectedBeneficiaryType = BeneficiaryType.INDIVIDUAL_BY_NAME;
      this.currentState = "person-dropdown";
    }
  }

  @HostListener("window:resize")
  onResize(): void {
    this.cardDensity = getCardDensity();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Rebuild dropdown when householdMembers input changes (fixes race condition when ZOK loads after component init)
    if (changes["householdMembers"] && !changes["householdMembers"].firstChange) {
      this.buildPersonDropdown();
    }

    // Repopulate form when existingData changes (edit mode)
    if (changes["existingData"] && !changes["existingData"].firstChange && this.mode === "edit") {
      this.populateFromExistingData();
    }
  }

  private populateFromExistingData(): void {
    if (!this.existingData) return;

    // Look up person data from householdPeople by householdMemberId
    if (this.existingData.householdMemberId) {
      const person = this.householdPeople.find(
        (p) => p.householdMemberId === this.existingData!.householdMemberId,
      );
      if (person) {
        this.firstName = person.name.firstName;
        this.lastName = person.name.lastName;
        this.dateOfBirth = person.birthDate || "";
        this.stateOfResidence = person.state || "";
      }
    }

    this.selectedBeneficiaryType = this.existingData.beneficiaryType;
    this.currentState = "inline-form";
  }

  private buildPersonDropdown(): void {
    const addNewItem: DropdownMenuItem = {
      labelText: content.BENEFICIARY_ADD_NEW_PERSON,
      id: "add-new-person",
    };

    this.personDropdownData = {
      groups: [
        {
          items: [addNewItem, ...this.householdMembers],
        },
      ],
    };
  }

  handleTypeSelection(event: RadioStateChangeEvent): void {
    const inputId = event?.inputId;
    if (inputId === "bene-type-individual") {
      this.selectedBeneficiaryType = BeneficiaryType.INDIVIDUAL_BY_NAME;
      this.currentState = this.mode === "edit" ? "inline-form" : "person-dropdown";
    } else if (inputId === "bene-type-trust") {
      this.selectedBeneficiaryType = BeneficiaryType.TRUST;
      this.currentState = "trust-placeholder";
    }
    this.logEvent("input_bene_card_type_selected", {
      type: this.selectedBeneficiaryType,
      next_state: this.currentState,
      mode: this.mode,
    });
  }

  handleChangeType(): void {
    this.currentState = "type-selection";
    this.selectedBeneficiaryType = null;
    // Only reset form in add mode; preserve data in edit mode
    if (this.mode === "add") {
      this.resetForm();
    }
  }

  handlePersonDropdownChange(event: DropdownStateChangeEvent): void {
    const selectedId = event?.activeItems?.[0];
    if (selectedId === "add-new-person") {
      this.logEvent("input_bene_card_add_new_person", { section: this.section });
      this.currentState = "inline-form";
      this.selectedPersonId = null;
    } else if (selectedId) {
      this.logEvent("input_bene_card_existing_person_selected", {
        member_id: selectedId,
        section: this.section,
      });
      this.selectedPersonId = selectedId;
      const selectedMember = this.householdMembers.find((m) => m.id === selectedId);
      if (selectedMember) {
        this.emitExistingPersonSelection(selectedMember);
      }
    }
  }

  handleInlineFormPersonChange(event: DropdownStateChangeEvent): void {
    const selectedId = event?.activeItems?.[0];
    if (selectedId && selectedId !== "add-new-person") {
      const selectedMember = this.householdMembers.find((m) => m.id === selectedId);
      if (selectedMember) {
        this.emitExistingPersonSelection(selectedMember);
      }
    }
  }

  private emitExistingPersonSelection(member: DropdownMenuItem): void {
    const memberId = member.id?.toString();
    if (!memberId) return;

    const person = this.householdPeople.find((p) => p.householdMemberId === memberId);

    this.logEvent("input_bene_card_emit_existing_person", {
      member_id: memberId,
      found_in_household: !!person,
      section: this.section,
    });
    const bene: Beneficiary = {
      kind: "existing",
      householdMemberId: memberId,
      type: this.section,
      distributionPercentage: 0,
      beneficiaryType: BeneficiaryType.INDIVIDUAL_BY_NAME,
    };
    this.save.emit(bene);
  }

  handleSave(): void {
    if (!this.validateForm()) {
      this.logEvent(
        "input_bene_card_save_validation_failed",
        {
          valid_first_name: this.validFirstName,
          valid_last_name: this.validLastName,
          valid_dob: this.validDateOfBirth,
          valid_state: this.validStateOfResidence,
        },
        "warn",
      );
      return;
    }

    const firstName = this.firstName.trim();
    const lastName = this.lastName.trim();
    const dateOfBirth = this.dateOfBirth;
    const stateOfResidence = this.stateOfResidence;
    const beneficiaryType = this.selectedBeneficiaryType || BeneficiaryType.INDIVIDUAL_BY_NAME;

    if (this.mode === "edit" && this.existingData?.householdMemberId) {
      // Edit mode: emit structured payload with separate concerns
      const payload: EditBeneficiaryPayload = {
        beneficiary: {
          kind: "existing",
          householdMemberId: this.existingData.householdMemberId,
          type: this.section,
          distributionPercentage: this.existingData.distributionPercentage,
          beneficiaryType,
        },
        personData: {
          firstName,
          lastName,
          dateOfBirth,
          stateOfResidence,
        },
      };

      this.logEvent("input_bene_card_save_edit_emitted", {
        member_id: payload.beneficiary.householdMemberId,
        section: payload.beneficiary.type,
        beneficiary_type: payload.beneficiary.beneficiaryType,
      });
      this.saveEdit.emit(payload);
      return;
    }

    // Add mode: create new beneficiary
    const bene: Beneficiary = {
      kind: "new",
      householdMemberId: null,
      personData: {
        firstName,
        lastName,
        dateOfBirth,
        stateOfResidence,
      },
      type: this.section,
      distributionPercentage: this.existingData?.distributionPercentage || 0,
      beneficiaryType,
    };

    this.logEvent("input_bene_card_save_emitted", {
      kind: bene.kind,
      section: bene.type,
      beneficiary_type: bene.beneficiaryType,
    });
    this.save.emit(bene);
  }

  handleCancel(): void {
    this.cancel.emit();
  }

  handleStateDropdownChange(event: DropdownStateChangeEvent): void {
    this.stateOfResidence = event?.activeItems?.[0] || "";
    this.validStateOfResidence = !!this.stateOfResidence;
  }

  handleDateChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.dateOfBirth = target?.value || "";
    this.validateDateOfBirth();
  }

  validateStateOfResidenceOnBlur(): void {
    setTimeout(() => {
      this.validStateOfResidence = !!this.stateOfResidence;
    }, 0);
  }

  // Called from template (blur) events for individual field validation
  validateFirstName(): void {
    this.validFirstName = !!this.firstName.trim();
  }
  validateLastName(): void {
    this.validLastName = !!this.lastName.trim();
  }
  validateDateOfBirth(): void {
    const result = validateDateOfBirthUtil(this.dateOfBirth, { required: true });
    this.validDateOfBirth = result.isValid;

    if (!result.isValid) {
      this.dateOfBirthErrorText = this.mapDateErrorToMessage(result.errorType);
    }
  }

  private mapDateErrorToMessage(errorType: DateValidationErrorType | null): string {
    switch (errorType) {
      case DateValidationErrorType.REQUIRED:
        return content.BENEFICIARY_FIELD_REQUIRED_ERROR;
      case DateValidationErrorType.FUTURE_DATE:
        return content.BENEFICIARY_DOB_FUTURE_ERROR;
      case DateValidationErrorType.PAST_DATE:
        return content.BENEFICIARY_DOB_PAST_ERROR;
      default:
        return content.BENEFICIARY_DOB_INVALID_ERROR;
    }
  }

  validateForm(): boolean {
    this.validateFirstName();
    this.validateLastName();
    this.validateDateOfBirth();
    this.validStateOfResidence = !!this.stateOfResidence;
    return (
      this.validFirstName &&
      this.validLastName &&
      this.validDateOfBirth &&
      this.validStateOfResidence
    );
  }

  private resetForm(): void {
    this.firstName = "";
    this.lastName = "";
    this.dateOfBirth = "";
    this.stateOfResidence = "";
    this.selectedPersonId = null;
    this.validFirstName = true;
    this.validLastName = true;
    this.validDateOfBirth = true;
    this.validStateOfResidence = true;
  }
}
